import { useState } from "react";
import { useLocation } from "react-router-dom";
import { stravaApi } from "../api/strava";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { analytics } from "@/services/analytics";

interface StravaConnectButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  /** Optional override for redirect URL after OAuth (defaults to current page) */
  redirectUrl?: string;
}

export function StravaConnectButton({ onError, redirectUrl }: StravaConnectButtonProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const location = useLocation();

  const handleConnect = async () => {
    try {
      setIsConnecting(true);

      // Track Strava connect attempt
      analytics.track.stravaConnectStarted();

      // Use provided redirectUrl or current page path
      const returnUrl = redirectUrl || location.pathname;

      // Get authorization URL from backend (works for both mobile and web)
      const { authorizationUrl } = await stravaApi.getAuthorizationUrl(returnUrl);

      if (Capacitor.isNativePlatform()) {
        // Mobile: Open in external browser
        await Browser.open({ url: authorizationUrl });
        // The app will handle the callback via deep link in AppUrlListener
      } else {
        // Web: Redirect to Strava OAuth page
        window.location.href = authorizationUrl;
      }
    } catch (error: any) {
      console.error("Failed to connect Strava:", error);
      onError?.(error.message || "Failed to connect Strava");
      setIsConnecting(false);
    }
  };

  return (
    <button
      onClick={handleConnect}
      disabled={isConnecting}
      className="flex items-center gap-4 rounded-3xl bg-white p-4 text-left shadow-sm ring-1 ring-subtle-light dark:bg-subtle-dark dark:ring-0 w-full hover:opacity-90 transition-opacity disabled:opacity-50"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FC4C02]/10 dark:bg-[#FC4C02]/20">
        <svg
          className="w-6 h-6 text-[#FC4C02]"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
        </svg>
      </div>
      <div className="flex-1">
        <p className="font-bold">
          {isConnecting ? "Connecting..." : "Connect to Strava"}
        </p>
        <p className="text-sm text-content-light/70 dark:text-content-dark/70">
          Sync your fitness activities automatically
        </p>
      </div>
      <span className="material-symbols-outlined text-content-light/50 dark:text-content-dark/50">
        chevron_right
      </span>
    </button>
  );
}
